import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, FileSignature, Calendar } from "lucide-react";
import { fmtDate, fmtEUR, statusLabel } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  property_id: "", tenant_id: "", signature_date: "", start_date: "", end_date: "",
  type: "nu", duration_months: 36, rent: 0, charges: 0, deposit: 0,
  irl_index: 0, resolution_clause: true, status: "actif",
};

export default function Baux() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => Promise.all([api.get("/leases"), api.get("/properties"), api.get("/tenants")]).then(([l, p, t]) => {
    setItems(l.data); setProps(p.data); setTenants(t.data);
  });
  useEffect(() => { load(); }, []);

  const start = (l) => { setEditing(l); setForm(l ? {...empty, ...l} : empty); setOpen(true); };
  const save = async () => {
    const body = {...form, rent: +form.rent, charges: +form.charges, deposit: +form.deposit, irl_index: +form.irl_index, duration_months: +form.duration_months};
    if (!body.property_id || !body.tenant_id || !body.start_date || !body.end_date) { toast.error("Bien, locataire et dates requis"); return; }
    if (!body.signature_date) body.signature_date = body.start_date;
    try {
      if (editing) { await api.put(`/leases/${editing.id}`, {...body, id: editing.id}); toast.success("Bail mis à jour"); }
      else { await api.post("/leases", body); toast.success("Bail créé · échéances générées"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!confirm("Supprimer ce bail et toutes ses échéances ?")) return; await api.delete(`/leases/${id}`); toast.success("Supprimé"); load(); };

  const tenantName = (id) => { const t = tenants.find(x => x.id === id); return t ? `${t.first_name} ${t.last_name}` : "—"; };
  const propLabel = (id) => { const p = props.find(x => x.id === id); return p ? p.address : "—"; };

  return (
    <div className="space-y-6" data-testid="baux-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-overline">Contrats</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Baux</h1>
          <p className="text-sm text-zinc-500 mt-1">{items.length} bail(s)</p>
        </div>
        <Button data-testid="new-lease-btn" onClick={() => start(null)}><Plus className="w-4 h-4 mr-1.5" />Nouveau bail</Button>
      </div>

      <Card className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">Bien</th><th className="px-4 py-3">Locataire</th>
              <th className="px-4 py-3">Période</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Loyer</th><th className="px-4 py-3">Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(l => (
              <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`lease-row-${l.id}`}>
                <td className="px-4 py-3 font-medium">{propLabel(l.property_id)}</td>
                <td className="px-4 py-3">{tenantName(l.tenant_id)}</td>
                <td className="px-4 py-3 text-xs"><div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(l.start_date)} → {fmtDate(l.end_date)}</div></td>
                <td className="px-4 py-3"><Badge variant="outline">{statusLabel[l.type]}</Badge></td>
                <td className="px-4 py-3 mono font-medium">{fmtEUR(l.rent + l.charges)}</td>
                <td className="px-4 py-3"><Badge className={l.status === "actif" ? "status-occupe" : "status-vacant"}>{statusLabel[l.status]}</Badge></td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => start(l)}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500"><FileSignature className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun bail.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Modifier le bail" : "Nouveau bail"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bien *</Label>
                <Select value={form.property_id} onValueChange={(v) => { const p = props.find(x => x.id === v); setForm({...form, property_id: v, rent: p?.rent || 0, charges: p?.charges || 0, deposit: p?.deposit || 0}); }}>
                  <SelectTrigger data-testid="lease-property"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{props.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locataire *</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({...form, tenant_id: v})}>
                  <SelectTrigger data-testid="lease-tenant"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Signature</Label><Input type="date" value={form.signature_date || ""} onChange={(e) => setForm({...form, signature_date: e.target.value})} /></div>
              <div><Label>Début *</Label><Input data-testid="lease-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>Fin *</Label><Input data-testid="lease-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="nu">Nu</SelectItem><SelectItem value="meuble">Meublé</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Durée (mois)</Label><Input type="number" value={form.duration_months} onChange={(e) => setForm({...form, duration_months: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Loyer HC</Label><Input type="number" value={form.rent} onChange={(e) => setForm({...form, rent: e.target.value})} /></div>
              <div><Label>Charges</Label><Input type="number" value={form.charges} onChange={(e) => setForm({...form, charges: e.target.value})} /></div>
              <div><Label>Dépôt garantie</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({...form, deposit: e.target.value})} /></div>
            </div>
            <div><Label>Indice IRL de référence</Label><Input type="number" step="0.01" value={form.irl_index} onChange={(e) => setForm({...form, irl_index: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="lease-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
